/* eslint-disable no-console */
import React, { useEffect, FC, MouseEvent, useState } from 'react';
// import * as Sentry from '@sentry/react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { HeaderKeys } from '@qred/shared-enums';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons/faCheckCircle';
import { IBaseComponent } from '~/sharedInterfaces/IBaseComponent';
import { getUrls } from '~/utils';
import { getDefaultLabelName } from '~/services/DefaultLabelNameService';
import { evaluateDefaultBankIntegrationSettings } from './BankIntegrationService';
import { IState } from '~/sharedInterfaces/IState';
import { IBankIntegrationSettings } from '~/sharedInterfaces/SharedInterfaces';
import ComponentError from '../ComponentError/ComponentError';
import Tooltip from '../Base/Tooltip/Tooltip';
import { AppearanceMode } from '~/sharedInterfaces/SharedEnums';
import { logUserFirstInteraction } from '~/actions/widgetContextActions';
import { setInput } from '~/actions/formActions';
import { BANK_INTEGRATION } from '~/actions/actionTypes';
import useKosmaScript from './KlarnaKosmaScript';

export interface IBankIntegration {
  bankIntegrationSettings?: IBankIntegrationSettings;
}

interface IBankIntegrationProps extends IBankIntegration, IBaseComponent {
  tooltip?: boolean;
}

const BankIntegration: FC<IBankIntegrationProps> = ({
  componentName,
  bankIntegrationSettings,
  tooltip,
  settings,
}) => {
  const dispatch = useDispatch();
  const { KOSMA_TOKEN_URL, KOSMA_SEND_TRANSACTION } = getUrls();
  const appState = useSelector((state: IState) => state);
  const { locale, accessToken, clientId } = useSelector((state: IState) => state.widgetContext);
  const [loading, setLoading] = useState(false);
  const { bankIntegration } = appState.onboarding;
  const { orgNumber } = appState.signup;
  const error = appState.error.bankIntegration?.missing;
  const defaultSettings = getDefaultLabelName(locale, componentName) as IBankIntegrationSettings;
  const { market, language } = settings;

  useKosmaScript();

  const {
    title,
    loading: loadingText,
    subTitle,
    buttonText,
    onSuccessText,
    tooltipText,
  } = evaluateDefaultBankIntegrationSettings(bankIntegrationSettings, defaultSettings);

  const sendTransactions = (response: any) => {
    dispatch(setInput(BANK_INTEGRATION, true));
    const { session_id: sessionId, flow_id: flowId } = response.data;
    const companyNumber = orgNumber || '';

    axios
      .put(
        KOSMA_SEND_TRANSACTION({ sessionId, flowId, market, orgNumber: companyNumber, clientId }),
        {},
        {
          headers: {
            [HeaderKeys.Authorization]: `${HeaderKeys.Bearer} ${accessToken}`,
          },
        }
      )
      .catch((e) => console.log(e));
  };

  const handleOnClick = (event: MouseEvent<HTMLInputElement>) => {
    event.preventDefault();
    setLoading(true);
    dispatch(logUserFirstInteraction());

    try {
      axios
        .post(
          KOSMA_TOKEN_URL(market, language),
          {},
          {
            headers: {
              [HeaderKeys.Authorization]: `${HeaderKeys.Bearer} ${accessToken}`,
            },
          }
        )
        .then((response) => {
          // override parts of the configuration just for this flow
          window.XS2A.startFlow(response.data.client_token, {
            onClose: () => setLoading(false),
            onError: () => dispatch(setInput(BANK_INTEGRATION, false)),
            onFinished: () => sendTransactions(response),
          });
        })
        .catch(() => {
          setLoading(false);
        });
    } catch (e) {
      // Handle error.
      console.log(e);
      setLoading(false);
    }
  };

  useEffect(() => {
    window.onXS2AReady = () => {
      // configure once for all flows (optional)
      window.XS2A.configure({
        appearanceMode: AppearanceMode.LIGHT,
        autoClose: true,
        hideTransitionOnFlowEnd: true,
        openPoliciesInNewWindow: true,
        psd2RedirectMethod: 'newWindow',
        skipInitialLoader: false,
        theme: 'default',
        unfoldConsentDetails: false,
      });
    };
  }, []);

  return (
    <div
      className={`qred-component qred-${componentName.toLowerCase()}${
        tooltip !== false ? ' qred-tooltip' : ''
      }${loading ? ' qred-loading' : ''}${error ? ' qred-error' : ''}`}
    >
      {tooltipText && <Tooltip tooltip={tooltip} tooltipText={tooltipText} />}
      <p className="qred-first">
        <span className="qred-first">{title}</span>
      </p>
      <p className="qred-second">
        <span className="qred-first">{subTitle}</span>
      </p>
      <div className="qred-group">
        <div className="qred-row">
          {bankIntegration ? (
            <p className="qred-third">
              <FontAwesomeIcon icon={faCheckCircle} />
              <span className="qred-first">{onSuccessText}</span>
            </p>
          ) : (
            <input
              type="button"
              onClick={handleOnClick}
              value={loading ? loadingText : buttonText}
              disabled={loading}
            />
          )}
          {error && <ComponentError error={error} />}
        </div>
      </div>
    </div>
  );
};

export default BankIntegration;
